import { createSlice } from "@reduxjs/toolkit";
import { Reel, Meta } from "src/store/types";
import {
  doFetchReelList,
  doFetchReportedReels,
  doBlockUnblockReel,
  doDeleteReel,
  doFetchReelReports,
  doPinUnpinReel,
} from "./reel.action";

interface ReelState {
  reels: Reel[];
  reportedReels: Reel[];
  meta: Meta | null;
  reportedMeta: Meta | null;
  loading: boolean;
  reportedLoading: boolean;
  error: string | null;
  searchQuery: string;
  reportedSearchQuery: string;
  reportDetails: [];
  reportDetailsLoading: boolean;
}

const initialState: ReelState = {
  reels: [] as Reel[],
  reportedReels: [] as Reel[],
  meta: null,
  reportedMeta: null,
  loading: false,
  reportedLoading: false,
  error: null,
  searchQuery: "",
  reportedSearchQuery: "",
  reportDetails: [],
  reportDetailsLoading: false,
};

export const reelSlice = createSlice({
  name: "reel",
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearch: (state) => {
      state.searchQuery = "";
    },
    setReportedSearchQuery: (state, action) => {
      state.reportedSearchQuery = action.payload;
    },
    clearReportedSearch: (state) => {
      state.reportedSearchQuery = "";
    },
  },
  extraReducers: (builder) => {
    // fetch reel list
    builder
      .addCase(doFetchReelList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doFetchReelList.fulfilled, (state, action) => {
        state.loading = false;
        state.reels = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(doFetchReelList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch reported reels
    builder
      .addCase(doFetchReportedReels.pending, (state) => {
        state.reportedLoading = true;
        state.error = null;
      })
      .addCase(doFetchReportedReels.fulfilled, (state, action) => {
        state.reportedLoading = false;
        state.reportedReels = action.payload.data;
        state.reportedMeta = action.payload.meta;
      })
      .addCase(doFetchReportedReels.rejected, (state, action) => {
        state.reportedLoading = false;
        state.error = action.payload as string;
      });

    // fetch reel reports
    builder
      .addCase(doFetchReelReports.pending, (state) => {
        state.reportDetailsLoading = true;
        state.error = null;
      })
      .addCase(doFetchReelReports.fulfilled, (state, action) => {
        state.reportDetailsLoading = false;
        state.reportDetails = action.payload.reports || [];
      })
      .addCase(doFetchReelReports.rejected, (state, action) => {
        state.reportDetailsLoading = false;
        state.error = action.payload as string;
      });

    // block/unblock reel
    builder
      .addCase(doBlockUnblockReel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doBlockUnblockReel.fulfilled, (state, action) => {
        state.loading = false;
        // Update the reel in both arrays if it exists
        if (action.payload.data) {
          const updatedPost = action.payload.data;
          state.reels = state.reels.map((reel) =>
            reel.pid === updatedPost.pid ? updatedPost : reel
          );
          state.reportedReels = state.reportedReels.map((reel) =>
            reel.pid === updatedPost.pid ? updatedPost : reel
          );
        }
      })
      .addCase(doBlockUnblockReel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // pin/unpin post
    builder
      .addCase(doPinUnpinReel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doPinUnpinReel.fulfilled, (state, action) => {
        state.loading = false;
        // Update the post in both arrays if it exists
        if (action.payload.data) {
          const updatedPost = action.payload.data;
          state.reels = state.reels.map((reel) =>
            reel.pid === updatedPost.pid ? updatedPost : reel
          );
          state.reportedReels = state.reportedReels.map((reel) =>
            reel.pid === updatedPost.pid ? updatedPost : reel
          );
        }
      })
      .addCase(doPinUnpinReel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // delete reel
    builder
      .addCase(doDeleteReel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doDeleteReel.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the reel from both arrays
        const deletedReelId = action.payload.pid;
        state.reels = state.reels.filter((reel) => reel.pid !== deletedReelId);
        state.reportedReels = state.reportedReels.filter(
          (reel) => reel.pid !== deletedReelId
        );
      })
      .addCase(doDeleteReel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  setSearchQuery,
  clearSearch,
  setReportedSearchQuery,
  clearReportedSearch,
} = reelSlice.actions;

export default reelSlice.reducer;
